import React from 'react';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { Button, Form, FormGroup } from 'reactstrap';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import intl from 'react-intl-universal';

import Icon from 'components/Icon';
import Radio from 'components/Radio';
import Page from 'components/Page';
import PageHeader from 'components/PageHeader';
import PageFooter from 'components/PageFooter';
import FormBlock from 'modules/forms/components/FormBlock';
import {
  EAppointmentFormStatus,
  EFormDisqualificationType,
  IForm,
} from 'modules/forms/interfaces/form.interfaces';
import { postAppointmentForm, setFormData } from 'modules/forms/actions/form.actions';
import { acuteSoreThroatRoutes } from 'modules/forms/constants/routes.constants';
import { useTypedSelector } from 'hooks/useTypedSelector';
import { getFormResultHTML } from 'modules/forms/helpers/formResult.helper';
import FormResult from './FormResult';

const Assessment = (): JSX.Element => {
  const history = useHistory();
  const dispatch = useDispatch();
  const appointment = useTypedSelector(state => state.form.appointment);
  const stateForm = useTypedSelector(state => state.form.form);
  const methods = useForm<IForm>({
    defaultValues: {
      hasSymptoms: stateForm.hasSymptoms,
    },
    mode: 'onSubmit',
  });
  const { control, watch, handleSubmit } = methods;
  const hasSymptoms = watch('hasSymptoms');

  const onBack = () => {
    history.goBack();
  };

  const onSubmit = (values: IForm) => {
    dispatch(setFormData(values));
    if (values.hasSymptoms === true) {
      dispatch(
        setFormData({
          disqualificationType: EFormDisqualificationType.TO_A_AND_E,
        }),
      );
      dispatch(
        postAppointmentForm({
          form: {
            appointmentUuid: appointment?.uuid,
            status: EAppointmentFormStatus.TO_A_AND_E,
            response: getFormResultHTML({ ...stateForm, ...values }, FormResult),
          },
          route: acuteSoreThroatRoutes.disqualification,
        }),
      );
    } else {
      history.push({
        pathname: acuteSoreThroatRoutes.secondaryAssessment,
        search: history.location.search,
      });
    }
  };

  return (
    <Page>
      <PageHeader step={3} maxSteps={8} />
      <FormProvider {...methods}>
        <Form className="page" onSubmit={handleSubmit(onSubmit)}>
          <div className="page-content">
            <main id="page-main-scrollable" className="page-main">
              <FormBlock
                title="Assessment"
                description="Is the patient for which you have booked this Acute Sore Throat consult experiencing extreme thirst, very dry mouth or feeling dizzy or lightheaded?"
              >
                <Controller
                  name="hasSymptoms"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <FormGroup>
                      <Radio
                        checked={value === true}
                        onChange={() => onChange(true)}
                        label="Yes, the patient is experiencing one or more of the above symptoms"
                      />
                      <Radio
                        checked={value === false}
                        onChange={() => onChange(false)}
                        label="No, the patient is not experiencing any of the above symptoms"
                      />
                    </FormGroup>
                  )}
                />
              </FormBlock>
            </main>

            <PageFooter>
              <Button color="primary" outline type="button" block onClick={onBack}>
                <Icon iconName="arrow-left" />
                {intl.get('button.back')}
              </Button>
              <Button
                color="primary"
                type="button"
                block
                disabled={hasSymptoms === undefined}
                onClick={handleSubmit(onSubmit)}
              >
                {intl.get('button.next')} <Icon iconName="arrow-right" />
              </Button>
            </PageFooter>
          </div>
        </Form>
      </FormProvider>
    </Page>
  );
};

export default Assessment;
